import Logo from 'assets/images/pmg-logo-beta.png'
import { P } from 'components'
import { useEffect, useState } from 'react'
import { ArrowLeft, Delete, Eye, EyeOff } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import { Button, ButtonGroup, Container, FormGroup, Input } from 'reactstrap'
import './otp.css'
import { useSendPinOtpMutation } from 'features/public-views/login-new/loginNewApiSlice'
import { validateEmail } from 'helpers'
import { recaptchaKey } from 'configs'
import useRecaptchaV3 from 'hooks/useRecaptchaV3'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import LoadingAnimation from 'components/LoadingAnimation'
import useClipboardStatus from 'hooks/useClipboardStatus'

const CardPin = ({
  title,
  desc,
  length,
  isOtp,
  handleResendOtp,
  handleSubmit,
  expireTime,
  loading,
  setErrMsg,
  errMsg,
  inputType = 'text',
  optionToShowPin,
  withButton = false,
  withPmImg,
  minHeight = '100vh',
  readOnly = true,
  onPressBack,
  isBack = true,
  showResetPin,
  isWithPaste = false,
  removePin,
  navigateScreen,
  testIdPrefix = 'pin',
}) => {
  const navigate = useNavigate()
  const [pin, setPin] = useState(new Array(length).fill(''))
  const [active, setActive] = useState('form-control-0')
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [keyboardType, setKeyboardType] = useState(inputType)
  const [isLoadingForgotPin, setLoadingForgotPin] = useState(false)
  const { identity } = useSelector((s) => s.auth)
  const { hasClipboardData } = useClipboardStatus()

  const { enqueueSnackbar } = useSnackbar()
  const [sendPinOtp] = useSendPinOtpMutation()
  const recaptcha = useRecaptchaV3(recaptchaKey)

  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = `
      .grecaptcha-badge {
        left: auto !important;
        top: 0 !important;
        bottom: auto !important;
        z-index: 9999 !important;
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [])

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const nextInput = (id) => {
    const input = document.getElementById(id)
    if (input) input.focus()
  }

  const handleKeypadClick = (number) => {
    if (setErrMsg) {
      setErrMsg('')
    }
    const nextIndex = pin.findIndex((p) => p === '')

    if (nextIndex !== -1) {
      const newPin = [...pin]
      newPin[nextIndex] = number
      setPin(newPin)

      if (nextIndex < 4) {
        setActive(`form-control-${nextIndex + 1}`)
        nextInput(`form-control-${nextIndex + 1}`)
      }
    }
  }

  const handleDelete = () => {
    if (setErrMsg) {
      setErrMsg('')
    }

    const lastIndex = pin.findIndex((p) => p === '')
    const indexToDelete = lastIndex === -1 ? pin.length - 1 : lastIndex - 1

    if (indexToDelete >= 0) {
      const newPin = [...pin]
      newPin[indexToDelete] = ''
      setPin(newPin)
      setActive(`form-control-${indexToDelete}`)
      nextInput(`form-control-${indexToDelete}`)
    }
  }

  const formatCountdown = (count) => {
    const minutes = Math.floor(count / 60)
    const seconds = count % 60
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  useEffect(() => {
    if (
      pin.length === length &&
      pin.every((pin) => pin !== '' && !loading) &&
      !withButton
    ) {
      handleSubmit(pin.join(''))
    }
  }, [pin])

  const getStoredBrowserData = () => ({
    browserHash: localStorage.getItem('browserHash') || null,
    browserFingerPrint: localStorage.getItem('browserFingerPrint') || null,
    browserCustomFingerPrint:
      localStorage.getItem('browserCustomFingerPrint') || null,
    deviceHash: localStorage.getItem('PMB_deviceHash') || null,
    fcmToken: localStorage.getItem('PMB_fcmToken') || null,
    mobileVersion: localStorage.getItem('PMB_mobileVersion') || null,
    packageName: localStorage.getItem('PMB_packageName') || null,
  })

  const onFetchOtp = async () => {
    setLoadingForgotPin(true)
    let storedData = getStoredBrowserData()
    try {
      const res = await sendPinOtp({
        ...storedData,
        identity: identity,
        type: 'resetPin',
        channel: validateEmail(identity) ? 'EMAIL' : 'WHATSAPP',
        'g-recaptcha-response': await recaptcha(),
      })
      if (res?.error?.data?.error) {
        throw res
      } else {
        navigate('/otp', {
          state: {
            type: 'resetPin',
            identity: identity,
            countDown: res?.data?.expireTime,
            status: 'resetPin',
            navigateScreen: navigateScreen,
            ...storedData,
          },
        })
      }
      setLoadingForgotPin(false)
    } catch (error) {
      setLoadingForgotPin(false)
      enqueueSnackbar(
        error?.error?.data?.meta?.message ||
          error?.message ||
          error?.data?.message ||
          error?.data?.error?.message ||
          error?.error?.data?.meta?.error ||
          error?.error?.data?.message ||
          'Terjadi kesalahan',
        {
          variant: 'error',
        },
      )
    }
  }

  const handlePaste = async () => {
    if (document.hasFocus()) {
      try {
        const text = await navigator.clipboard.readText()
        setPin(
          text
            .trim()
            .slice(0, length)
            .split('')
            .concat(new Array(length - text.trim().length).fill('')),
        )
      } catch (error) {
        console.error('Clipboard Error:', error)
        alert(
          'Gagal menempel text. Pastikan Anda mengizinkan akses clipboard di browser dan halaman ini sedang aktif.',
        )
      }
    } else {
      alert(
        'Halaman ini tidak dalam fokus. Silakan klik pada halaman terlebih dahulu sebelum menempel.',
      )
    }
  }

  return (
    <Container
      style={{ minHeight: minHeight, maxWidth: '600px' }}
      className="d-flex flex-column justify-content-between py-3 w-100"
    >
      {isBack ? (
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (onPressBack) {
              onPressBack()
            } else {
              navigate(-1)
            }
          }}
          className="d-flex justify-content-start d-block"
          data-testid={`${testIdPrefix}-back-button`}
        >
          <ArrowLeft size={20} />
        </div>
      ) : (
        <div />
      )}

      {withPmImg ? (
        <img
          src={Logo}
          alt="Logo PasarMikro"
          className="img-web-only"
          style={{
            marginBottom: 24,
            maxWidth: '177px',
            height: 'auto',
          }}
        />
      ) : null}

      <div
        className="bg-white rounded mt-0 mt-md-4 shadow-web"
        style={{ borderRadius: 99 }}
      >
        {/* Header Section */}
        <div>
          <P size="large">{title}</P>
          {desc ? (
            <P size="regular" color={'#475467'}>
              {desc}
            </P>
          ) : null}
        </div>

        {/* PIN */}
        <div className="d-flex flex-column">
          <div className="mt-2">
            <P className="text-danger" size="regular" testId={`${testIdPrefix}-error-message`}>
              {errMsg}
            </P>
          </div>
          <FormGroup className="d-flex gap-3 mt-4">
            {[...Array(length)].map((_, i) => (
              <div key={i}>
                <div
                  className="position-absolute top-0 start-0 w-0 h-0"
                  style={{ zIndex: 2, backgroundColor: 'transparent' }}
                  onClick={() => nextInput(active)}
                ></div>
                <Input
                  disabled={loading}
                  type={keyboardType}
                  readOnly={readOnly}
                  style={{
                    zIndex: 1,
                    fontSize: 30,
                    textAlign: 'center',
                  }}
                  autoFocus={i === 0}
                  className={`w-100 text-center ${
                    isMobile
                      ? 'fs-2 border border-dark border-top-0 border-start-0 border-end-0 rounded-0'
                      : ''
                  }`}
                  id={`form-control-${i}`}
                  key={i}
                  maxLength={1}
                  data-testid={`${testIdPrefix}-input-${i}`}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onFocus={(e) => {
                    e.target.style.boxShadow = 'none'
                  }}
                  value={pin[i]}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    const value = e.target.value

                    if (!e.code.includes('Digit') && e.code !== 'Backspace') {
                      e.preventDefault()
                    } else if (e.code === 'Backspace') {
                      if (i > 0 || pin[i] !== '') {
                        setPin((prevPin) => {
                          const newPin = [...prevPin]
                          newPin[i] = ''
                          return newPin
                        })
                        setActive(`form-control-${i - 1}`)
                        nextInput(`form-control-${i - 1}`)
                      }
                    } else if (e.code?.includes('Digit')) {
                      if (value.match(/^[0-9]$/)) {
                        setPin((prev) => {
                          const newPin = [...prev]
                          newPin[i] = value
                          return newPin
                        })

                        if (e.target.id !== 'form-control-5') {
                          setActive(`form-control-${i + 1}`)
                          nextInput(`form-control-${i + 1}`)
                        }
                      }
                    } else if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                      e.preventDefault()
                      navigator.clipboard.readText().then((pasteData) => {
                        const cleanedData = pasteData.trim().replace(/\D/g, '')
                        if (cleanedData.length > 0) {
                          setPin((prev) => {
                            const newPin = [...prev]
                            cleanedData.split('').forEach((char, index) => {
                              if (index < 5) {
                                newPin[index] = char
                              }
                            })
                            return newPin
                          })
                          nextInput('form-control-4')
                        }
                      })
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault()
                    const pasteData = e.clipboardData
                      .getData('Text')
                      .trim()
                      .replace(/\D/g, '')
                    if (pasteData.length > 0) {
                      setPin((prev) => {
                        const newPin = [...prev]
                        pasteData.split('').forEach((char, index) => {
                          if (index < 5) {
                            newPin[index] = char
                          }
                        })
                        return newPin
                      })
                      nextInput('form-control-4')
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value

                    if (value.match(/^[0-9]$/)) {
                      setPin((prev) => {
                        const newPin = [...prev]
                        newPin[i] = value
                        return newPin
                      })

                      if (e.target.id !== 'form-control-4') {
                        setActive(`form-control-${i + 1}`)
                        nextInput(`form-control-${i + 1}`)
                      }
                    }
                  }}
                />
              </div>
            ))}
          </FormGroup>

          {optionToShowPin ? (
            <div
              style={{
                justifyContent: 'center',
                display: 'flex',
                gap: 4,
                cursor: 'pointer',
                marginTop: 20,
              }}
              onClick={() => {
                if (keyboardType === 'password') {
                  setKeyboardType('text')
                } else {
                  setKeyboardType('password')
                }
              }}
              data-testid={`${testIdPrefix}-show-pin-toggle`}
            >
              {keyboardType === 'password' ? <Eye /> : <EyeOff />}
              {keyboardType === 'password' ? (
                <P>Lihat pin</P>
              ) : (
                <P>Sembunyikan pin</P>
              )}
            </div>
          ) : null}

          {/* Show/Hide PIN */}
          {isOtp ? (
            <div
              onClick={() => {
                if (expireTime === 0) {
                  handleResendOtp()
                }
              }}
            >
              <P
                size="regular"
                color={'#475467'}
                className={expireTime > 0 ? 'text-muted' : ''}
              >
                Tidak menerima kode?{' '}
                <span
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  data-testid={`${testIdPrefix}-resend-code-link`}
                >
                  Klik untuk kirim ulang kode
                </span>{' '}
                <span data-testid={`${testIdPrefix}-countdown`}>
                  ({formatCountdown(expireTime)})
                </span>
              </P>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center" data-testid={`${testIdPrefix}-loading-indicator`}>
          <LoadingAnimation color={'primary'} />
        </div>
      ) : null}

      {showResetPin ? (
        <div
          style={{
            justifyContent: 'center',
            display: 'flex',
            gap: 4,
            cursor: 'pointer',
            marginTop: 20,
          }}
          onClick={onFetchOtp}
          data-testid={`${testIdPrefix}-forgot-pin-link`}
        >
          {isLoadingForgotPin ? (
            <LoadingAnimation size={'sm'} color={'primary'} />
          ) : (
            <P color={'#006386'}>Lupa PIN?</P>
          )}
        </div>
      ) : null}

      {/* Keypad & button*/}
      <div style={{ opacity: loading ? 0.5 : 1 }}>
        {withButton ? (
          <div className="w-100">
            <Button
              className="w-100"
              color="primary"
              disabled={
                pin.every((pin) => pin !== '') && loading === false
                  ? false
                  : true
              }
              onClick={async () => {
                await handleSubmit(pin.join(''))
                if (removePin) {
                  setPin(['', '', '', '', '', ''])
                }
              }}
              data-testid={`${testIdPrefix}-submit-button`}
            >
              Lanjut
            </Button>
          </div>
        ) : null}
        <div className="d-flex flex-column mt-4 mb-5">
          <ButtonGroup className="d-flex justify-content-center">
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border border-top-0 border-start-0 rounded-0 p-3"
              size="lg"
              onClick={() => handleKeypadClick(1)}
              data-testid={`${testIdPrefix}-keypad-1`}
            >
              1
            </Button>
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border border-top-0 rounded-0 p-3"
              size="lg"
              onClick={() => handleKeypadClick(2)}
              data-testid={`${testIdPrefix}-keypad-2`}
            >
              2
            </Button>
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border border border-end-0 border-top-0 rounded-0 p-3"
              size="lg"
              onClick={() => handleKeypadClick(3)}
              data-testid={`${testIdPrefix}-keypad-3`}
            >
              3
            </Button>
          </ButtonGroup>
          <ButtonGroup className="d-flex justify-content-center">
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border border-top-0 border-start-0 rounded-0 p-3"
              size="lg"
              onClick={() => handleKeypadClick(4)}
              data-testid={`${testIdPrefix}-keypad-4`}
            >
              4
            </Button>
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border rounded-0 p-3 border-top-0"
              size="lg"
              onClick={() => handleKeypadClick(5)}
              data-testid={`${testIdPrefix}-keypad-5`}
            >
              5
            </Button>
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border border-top-0 border border-end-0 rounded-0 p-3"
              size="lg"
              onClick={() => handleKeypadClick(6)}
              data-testid={`${testIdPrefix}-keypad-6`}
            >
              6
            </Button>
          </ButtonGroup>
          <ButtonGroup className="d-flex justify-content-center">
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border border-top-0 border-start-0 rounded-0 p-3"
              size="lg"
              onClick={() => handleKeypadClick(7)}
              data-testid={`${testIdPrefix}-keypad-7`}
            >
              7
            </Button>
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border rounded-0 p-3 border-top-0"
              size="lg"
              onClick={() => handleKeypadClick(8)}
              data-testid={`${testIdPrefix}-keypad-8`}
            >
              8
            </Button>
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border border-top-0 border border-end-0 rounded-0 p-3"
              size="lg"
              onClick={() => handleKeypadClick(9)}
              data-testid={`${testIdPrefix}-keypad-9`}
            >
              9
            </Button>
          </ButtonGroup>
          <ButtonGroup className="d-flex justify-content-center">
            <Button
              block
              outline
              color="primary"
              className="border border-bottom-0 border-top-0 border-start-0 rounded-0 p-3"
              size="lg"
              // disabled={!hasClipboardData}
              onClick={() => {
                if (isWithPaste) {
                  setTimeout(() => handlePaste(), 0)
                }
              }}
              data-testid={`${testIdPrefix}-paste-button`}
            >
              {isWithPaste ? 'Tempel' : ''}
            </Button>
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border border-bottom-0 border-top-0 rounded-0 p-3"
              size="lg"
              onClick={() => handleKeypadClick(0)}
              data-testid={`${testIdPrefix}-keypad-0`}
            >
              0
            </Button>
            <Button
              disabled={loading}
              block
              outline
              color="primary"
              className="border border-top-0 border-bottom-0 border border-end-0 rounded-0 p-3"
              size="lg"
              onClick={handleDelete}
              data-testid={`${testIdPrefix}-keypad-delete`}
            >
              <Delete size={20} />
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </Container>
  )
}

export default CardPin
